export type CoreCapabilityKey = "mint" | "redeem";
export type CapabilityGrantState = "Granted" | "Suspended" | "Revoked";
export type CapabilityGrantAccessAction = "GRANT_PAIR" | "SUSPEND_PAIR" | "RESTORE_PAIR" | "REVOKE_PAIR";
export type CapabilityEvidenceAction = "INVALIDATE_EVIDENCE" | "REINSTATE_EVIDENCE";
export type CapabilityAccessAction = CapabilityGrantAccessAction | CapabilityEvidenceAction;
export type CapabilityGrantAction = "GRANT" | "SUSPEND" | "RESTORE" | "REVOKE";

const CAPABILITY_ACTIONS: Record<CapabilityGrantAccessAction, { command: CapabilityGrantAction; label: string }> = {
    GRANT_PAIR: { command: "GRANT", label: "Grant Mint and Redeem" },
    SUSPEND_PAIR: { command: "SUSPEND", label: "Suspend Mint and Redeem" },
    RESTORE_PAIR: { command: "RESTORE", label: "Restore Mint and Redeem" },
    REVOKE_PAIR: { command: "REVOKE", label: "Revoke Mint and Redeem" }
};

export function capabilityGrantActionFor(action: CapabilityAccessAction): CapabilityGrantAction {
    if (!isCapabilityGrantAccessAction(action)) throw new Error(`${action} is not a grant action`);
    return CAPABILITY_ACTIONS[action].command;
}

export function capabilityAccessActionLabel(action: CapabilityAccessAction, repair: boolean): string {
    if (action === "INVALIDATE_EVIDENCE") return "Invalidate evidence";
    if (action === "REINSTATE_EVIDENCE") return "Reinstate evidence";
    return action === "GRANT_PAIR" && repair ? "Repair Mint and Redeem" : CAPABILITY_ACTIONS[action].label;
}

export function isCapabilityGrantAccessAction(action: CapabilityAccessAction): action is CapabilityGrantAccessAction {
    return action in CAPABILITY_ACTIONS;
}
export type CapabilityAccessBlocker =
    | "CAPABILITY_NOT_GRANTED"
    | "CAPABILITY_GRANT_SUSPENDED"
    | "CAPABILITY_GRANT_REVOKED"
    | "ISSUER_CLIENT_PENDING"
    | "ISSUER_CLIENT_SUSPENDED"
    | "ISSUER_CLIENT_CANCELLED"
    | "ISSUER_CAPABILITY_REQUIRED"
    | "ISSUER_CAPABILITY_SUSPENDED"
    | "CAPABILITY_REQUIREMENT_INCOMPLETE"
    | "CAPABILITY_REQUIREMENT_VERSION_NOT_EFFECTIVE"
    | "AGREEMENT_NOT_STARTED"
    | "AGREEMENT_IN_PROGRESS"
    | "AGREEMENT_VERIFICATION_PENDING"
    | "AGREEMENT_EVIDENCE_INELIGIBLE"
    | "AGREEMENT_ARTIFACT_INVALID"
    | "AGREEMENT_ARTIFACT_NOT_ALLOWED_IN_ENVIRONMENT"
    | "CAPABILITY_ACCESS_EVALUATION_FAILED";

export type CapabilityAccessPresentationState =
    | "STATUS_UNAVAILABLE"
    | "ACCESS_REMOVED"
    | "NOT_AVAILABLE"
    | "PARTIAL_ACCESS"
    | "MIXED_STATUS"
    | "SUSPENDED"
    | "ACTION_REQUIRED"
    | "AGREEMENT_PENDING"
    | "VERIFICATION_PENDING"
    | "PENDING_ACTIVATION"
    | "ENABLED"
    | "ENABLED_UPDATE_REQUIRED"
    | "BLOCKED";

export interface CapabilityAccessDecision {
    allowed: boolean;
    evaluatedAt: string;
    capabilityKey: CoreCapabilityKey;
    grantUuid: string | null;
    grantState: CapabilityGrantState | null;
    effectiveRequirementUuid: string | null;
    satisfyingSource: { type: "verified_agreement" | "onboarding_override"; uuid: string } | null;
    blockers: CapabilityAccessBlocker[];
}

/**
 * The operator-facing lifecycle of an agreement envelope, as yaas-api projects
 * it. Shared by the per-client capability-access view and the issuer's
 * Agreements list, so a row on the list says the same word as the client's own
 * page.
 */
export type AgreementStage =
    | "Setup"
    | "ReadyToSend"
    | "Preparing"
    | "Sent"
    | "Delivered"
    | "PartiallySigned"
    | "VerificationPending"
    | "Verified"
    | "Declined"
    | "Voided"
    | "Expired"
    | "DeliveryFailed"
    | "Failed";

/**
 * What each stage is called wherever an agreement is shown.
 *
 * `VerificationPending` and `Verified` are the wire values, but neither word
 * describes what has happened: the first means the client has signed and the
 * counter-signature is outstanding, the second that both parties have signed.
 * The labels say that instead. Nothing is verified at either point. The
 * archival of the executed copy that the word came from happens after
 * signing, and is shown to nobody.
 */
export const AGREEMENT_STAGE_LABEL: Record<AgreementStage, string> = {
    Setup: "Setup",
    ReadyToSend: "Ready to send",
    Preparing: "Preparing",
    Sent: "Sent",
    Delivered: "Opened by company recipient",
    PartiallySigned: "Partially signed",
    VerificationPending: "Awaiting counter-signature",
    Verified: "Signed",
    Declined: "Declined",
    Voided: "Voided",
    Expired: "Expired",
    DeliveryFailed: "Delivery failed",
    Failed: "Failed"
};

export interface CapabilityAccessView {
    issuerClientUuid: string;
    rolloutAvailable: boolean;
    evaluatedAt: string;
    grants: {
        mint: { uuid: string; state: CapabilityGrantState } | null;
        redeem: { uuid: string; state: CapabilityGrantState } | null;
    };
    mint: CapabilityAccessDecision;
    redeem: CapabilityAccessDecision;
    requirements: Array<{
        uuid: string;
        capabilityKeys: CoreCapabilityKey[];
        satisfyingSource: CapabilityAccessDecision["satisfyingSource"];
    }>;
    futureRequirements?: Array<{
        uuid: string;
        moduleVersion: number;
        effectiveFrom: string;
        stage: CapabilityAccessView["agreement"]["stage"];
        incomplete: boolean;
    }>;
    availableRollouts?: Array<{
        policyRevisionUuid: string;
        targetVersionUuid: string;
        targetVersion: number;
        policyType: "Optional" | "Requested";
        responseState: "PartiallyAdopted" | "Adopted" | "Deferred" | "Declined" | "Superseded" | null;
        allowedActions: Array<"ADOPT_VERSION" | "DEFER_ROLLOUT" | "DECLINE_ROLLOUT">;
    }>;
    agreement: {
        requirementUuid: string | null;
        stage: AgreementStage;
        version: number | null;
        documentVersion: string | null;
        useClass: "Test" | "Production" | null;
        permittedActions: string[];
        documentKinds?: Array<"signed-document" | "completion-certificate">;
        override?: {
            uuid: string;
            actorType: string;
            actorIdentity: string;
            authority: string;
            reason: string;
            createdAt: string;
        } | null;
        evidenceEligibility?: {
            evidenceSourceType: "AgreementEnvelope";
            evidenceSourceUuid: string;
            status: "Eligible" | "Ineligible";
            decidedAt: string | null;
        } | null;
    };
    allowedActions: CapabilityAccessAction[];
    presentationState: CapabilityAccessPresentationState;
}
